/* eslint-disable ember/use-ember-get-and-set */
import Service, { service } from '@ember/service';
import type Store from '@ember-data/store';
import { restartableTask, timeout } from 'ember-concurrency';
import config from 'garaje/config/environment';
import type ZoneModel from 'garaje/models/zone';
import type MqttConnectionService from 'garaje/services/mqtt-connection';
import type MqttDispatcherService from 'garaje/services/mqtt-dispatcher';

interface ChannelConnectInviteEventData {
  event: 'new_connect_invite' | 'updated_connect_invite';
  connect_invite?: {
    data: Record<string, string>;
  };
}

interface ChannelConnectDeletedInviteEventData {
  event: 'deleted_connect_invite';
  connect_invite?: {
    data: Record<string, string>;
  };
}

interface ChannelConnectInviteReviewedEventData {
  event: 'reviewed_connect_invite';
  action: 'approve' | 'deny';
  reviewer_name: string;
  tenant_name: string;
  visitor_name: string;
}

export type ChannelConnectEventData =
  | ChannelConnectInviteEventData
  | ChannelConnectDeletedInviteEventData
  | ChannelConnectInviteReviewedEventData;

export default class CurrentZoneService extends Service {
  @service declare mqttConnection: MqttConnectionService;
  @service declare mqttDispatcher: MqttDispatcherService;
  @service declare store: Store;

  zone?: ZoneModel | null;

  /**
   * Used to indicate that the main zones list is
   * stale and needs to be refreshed when re-entering the the current-zone route.
   */
  zonesAreStale = false;

  setup(zone: ZoneModel): void {
    if (zone.id !== this.zone?.id) {
      this.teardown();
    }

    this.#setupMqtt(zone.id);
    this.zone = zone;
    void this.pollDevicesStatus.perform();
  }

  teardown(): void {
    void this.pollDevicesStatus.cancelAll();
    this.#teardownMqtt();
    this.zone = null;
  }

  pollDevicesStatus = restartableTask(async () => {
    if (config.environment === 'test') return;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      await timeout(300000);
      if (!this.zone?.id) return;
      await this.store.query('device', { filter: { zone: this.zone.id }, include: 'printer' });
    }
  });

  subscribeToConnectInvites(onMessage: (payload: ChannelConnectEventData) => unknown): void {
    if (!this.zone?.id) return;

    this.mqttDispatcher.setZoneContext({
      zoneId: this.zone.id,
      onConnectInvite: (payload) => onMessage(payload as unknown as ChannelConnectEventData),
    });
  }

  unsubscribeToConnectInvites(): void {
    if (!this.zone?.id) return;

    // Reset zone context without the connect-invite handler — keeps device-event
    // dispatch intact while clearing the connect-invites subscription.
    this.mqttDispatcher.setZoneContext({ zoneId: this.zone.id });
  }

  #setupMqtt(zoneId: string): void {
    this.mqttDispatcher.setZoneContext({ zoneId });

    void this.mqttConnection.start({ zoneId }, (msg) => {
      this.mqttDispatcher.dispatchZoneMessage(msg, zoneId);
    });
  }

  #teardownMqtt(): void {
    this.mqttDispatcher.clearZoneContext();
    void this.mqttConnection.stop();
  }
}
